import {
    Checkbox as FluentCheckbox,
    ICheckboxProps
} from "@fluentui/react/lib/Checkbox"
import { Label } from "@fluentui/react/lib/Label"
import { Stack, StackItem } from "@fluentui/react/lib/Stack"
import { IProcessedStyleSet, mergeStyles } from "@fluentui/react/lib/Styling"
import { TooltipHost, TooltipOverflowMode } from "@fluentui/react/lib/Tooltip"
import { classNamesFunction, styled } from "@fluentui/react/lib/Utilities"
import React from "react"

import {
    Constants as RestApiConstants,
    selectedRawValue,
    specState,
    Value
} from "@encoway/c-services-js-client"
import { ComponentFactory } from "@encoway/react-configurator"

import {
    ComponentName,
    SPEC_STATE_SET_BY_SYSTEM,
    READY_STATE_NOT_READY
} from "../../constants"
import {
    determineBinaryZeroValue,
    determineBinaryOneValue
} from "../../helperFunctions"
import { CheckboxStyles } from "./Checkbox.styles"
import { CheckboxProps, ICheckboxStyles } from "./Checkbox.types"

const renderLabel = (
    labelProps: ICheckboxProps,
    classNames: IProcessedStyleSet<ICheckboxStyles>,
    onValueChanged: () => void
) => {
    return (
        <Stack
            horizontal
            tokens={{ childrenGap: 10 }}
            onClick={(e) => e.preventDefault()}
            style={{ minWidth: 0 }}
        >
            <Label
                disabled={labelProps.disabled}
                className={classNames.label}
                onClick={
                    labelProps.disabled ? undefined : () => onValueChanged()
                }
                style={labelProps.disabled ? {} : { cursor: "pointer" }}
            >
                <TooltipHost
                    hostClassName={classNames.textContainer}
                    overflowMode={TooltipOverflowMode.Self}
                    content={labelProps.label}
                >
                    {labelProps.label}
                </TooltipHost>
            </Label>
        </Stack>
    )
}

interface PositionPriceProps extends CheckboxProps {
    priceOfTrueValue?: number
    checked: boolean
}

const PositionPrice = (props: PositionPriceProps) => {
    let positionPrice
    const { data, options, priceOfTrueValue, checked } = props
    const currency = options?.currency
    const showPrices = options?.showPrices
    if (checked) {
        positionPrice = ComponentFactory.instanceOf(ComponentName.Price, {
            ...props,
            price:
                data.totalPrice !== 0 || priceOfTrueValue === 0
                    ? data.totalPrice
                    : null,
            currency,
            showPrices
        })
    } else if (!props.options?.readOnly) {
        positionPrice = ComponentFactory.instanceOf(ComponentName.Price, {
            ...props,
            price: priceOfTrueValue,
            currency,
            showPrices
        })
    }
    return positionPrice || null
}

const Quantity = (props: any) => {
    return ComponentFactory.instanceOf(ComponentName.Quantity, props)
}
const StateIcon = (props: any) => {
    return ComponentFactory.instanceOf(ComponentName.StateIcon, props)
}
const InfoButton = (props: any) => {
    return ComponentFactory.instanceOf(ComponentName.InfoButton, props)
}
const BurgerMenu = (props: any) => {
    return ComponentFactory.instanceOf(ComponentName.BurgerMenu, props)
}
/**
 * The Checkbox component renders a ParameterTO as a headline using the Parameter component and its ValueTOs as checkbox.
 *
 * Links:
 * - [Checkout the code](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/configurator-components/src/components/Checkbox/Checkbox.tsx)
 * - [CheckboxStyles](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/configurator-components/src/components/Checkbox/Checkbox.styles.ts)
 * - [CheckboxProps](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/configurator-components/src/components/Checkbox/Checkbox.types.ts)
 * - [MS Fluent Checkbox](https://developer.microsoft.com/de-DE/fluentui#/controls/web/checkbox)
 *
 * @visibleName Checkbox
 */
const ICheckbox = (props: CheckboxProps) => {
    const { styles, theme, ...delegatedProps } = props
    const classNames = classNamesFunction()(
        styles,
        theme
    ) as IProcessedStyleSet<ICheckboxStyles>
    const specStateValue = specState(props.data)
    const isEditable = props.data.editable && !props.options?.readOnly
    const values: Value[] = props.data.values || []
    const falseValue = determineBinaryZeroValue(values)
    const trueValue = determineBinaryOneValue(values, falseValue)

    const priceOfTrueValue = trueValue?.price
    const value = selectedRawValue(props.data, falseValue?.value)
    const checked = value === trueValue?.value

    const onValueChanged = () => {
        if (!checked) {
            props.onValueChanged(
                props.data,
                trueValue?.value,
                RestApiConstants.ValueFormat.Unformatted
            )
        } else {
            props.onValueChanged(
                props.data,
                falseValue?.value,
                RestApiConstants.ValueFormat.Unformatted
            )
        }
    }

    let rootClassName = classNames.root
    if (props.data.readyState === READY_STATE_NOT_READY) {
        rootClassName = mergeStyles(rootClassName, classNames.mandatoryRoot)
    }

    let className = classNames.checkbox
    if (!isEditable) {
        className = mergeStyles(className, classNames.disabled)
    } else if (specStateValue === SPEC_STATE_SET_BY_SYSTEM) {
        className = mergeStyles(className, classNames.conflict)
    }

    return (
        <Stack className={rootClassName}>
            <Stack
                horizontal
                tokens={{ childrenGap: "0.5em" }}
                className={classNames.checkboxContainer}
            >
                <FluentCheckbox
                    className={className}
                    disabled={!isEditable}
                    label={props.data.translatedName}
                    onRenderLabel={(p) =>
                        renderLabel(p || {}, classNames, onValueChanged)
                    }
                    boxSide={props.checkboxPosition || "start"}
                    checked={checked}
                    onChange={onValueChanged}
                />
                <InfoButton {...delegatedProps} />
                <StackItem grow={1}>
                    <Quantity {...delegatedProps} />
                </StackItem>
                <StackItem
                    className={!checked ? classNames.price : undefined}
                    style={checked ? { marginRight: "1em" } : undefined}
                >
                    <PositionPrice
                        {...delegatedProps}
                        priceOfTrueValue={priceOfTrueValue}
                        checked={checked}
                    />
                </StackItem>
                <StateIcon {...delegatedProps} />
                <BurgerMenu {...delegatedProps} />
            </Stack>
        </Stack>
    )
}
/**
 * Renders a checkbox for a model position.
 * The Checkbox component renders a checkbox for a given model parameter. The state of the checkbox derives from the values of the checkbox (see determineFalseValue and determineTrueValue).
 *
 * @see CheckboxStyles
 * @see CheckboxProps
 */
export const Checkbox = styled(ICheckbox, CheckboxStyles)
Checkbox.displayName = "Checkbox"
